import React, { useState } from "react";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import {
  AssetType,
  useAddChildrenToParentAssetMutation,
  useAddParentsToChildAssetMutation,
  useGetAssetsQuery,
  useGetChildrenAssetsQuery,
  useGetParentAssetsQuery,
  useRemoveChildrenFromParentAssetMutation,
  useRemoveParentsFromChildAssetMutation,
} from "../store/slices/mapsSlice";
import AssetList from "./AssetList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

export default function AssetRelations({
  selectedAsset,
  relationType,
}: {
  relationType?: string;
  selectedAsset: Partial<AssetType>;
}) {
  const assets = useGetAssetsQuery({ page: 0, perPage: 10, search: "" });
  const { data: { assets: childrenAssets } = { assets: [] } } =
    useGetChildrenAssetsQuery(selectedAsset?.Uuid ?? "", {
      skip: !selectedAsset?.Uuid,
    });
  const { data: { assets: parentAssets } = { assets: [] } } =
    useGetParentAssetsQuery(selectedAsset?.Uuid ?? "", {
      skip: !selectedAsset?.Uuid,
    });

  const [newChildAssets, setNewChildAssets] = useState<string[]>([]);
  const [newParentAssets, setNewParentAssets] = useState<string[]>([]);

  const [setAsParentAsset] = useAddChildrenToParentAssetMutation();
  const [setAsChildAsset] = useAddParentsToChildAssetMutation();
  const [removeChildrenFromParentAsset] =
    useRemoveChildrenFromParentAssetMutation();
  const [removeParentsFromChildAsset] =
    useRemoveParentsFromChildAssetMutation();

  return (
    <div className="p-3">
      <div>
        <div>
          <span className="fw-bold">Asset name:</span>
          <span className="text-secondary ms-2">{selectedAsset.Label}</span>
        </div>
        <div>
          <span className="fw-bold">Asset description:</span>
          <span className="text-secondary ms-2">
            {selectedAsset.Description}
          </span>
        </div>
        <div className="text-secondary">
          Connect multiple assets to this one or create Asset dependencies
        </div>
      </div>
      {relationType !== "connected" && (
        <div className="bg-purple-gradient-darker-reverse p-3 rounded-4 my-3">
          <div className="mb-5 mt-2">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <div className="">
                <div className="fs-5 fw-bold"> Dependant Assets</div>
                <div className="text-secondary text-nowrap">
                  Create dependent assets under the principal asset
                </div>
              </div>
              <div className="d-flex gap-1">
                <Input
                  key={childrenAssets?.length + parentAssets?.length}
                  // label="Add child assets"
                  placeholder="Select dependant assets"
                  value={newChildAssets}
                  onChange={setNewChildAssets}
                  options={(assets.data?.assets || [])
                    .filter((a) => a.Uuid !== selectedAsset?.Uuid)
                    .filter(
                      (a) =>
                        !childrenAssets?.some((child) => child.Uuid === a.Uuid)
                    )
                    .filter(
                      (a) =>
                        !parentAssets?.some((parent) => parent.Uuid === a.Uuid)
                    )
                    .map((a: any) => ({
                      label: a.Label,
                      value: a.Uuid,
                    }))}
                  formatOptionLabel={({ label, value }: any) => {
                    const a = assets.data?.assets.find(
                      (asset) => asset.Uuid === value
                    );
                    return (
                      <div>
                        <div>
                          <span className="fw-bold">Name:</span>
                          <span className="text-secondary ms-2">
                            {a?.Label}
                          </span>
                        </div>
                        <div>
                          <span className="fw-bold"> Ref:</span>
                          <span className="text-secondary ms-2">{a?.Uuid}</span>
                        </div>
                        <span className="fw-bold">Cust Ref:</span>
                        <span className="text-secondary ms-2">
                          {a?.Reference}
                        </span>

                        <div
                          className="text-truncate"
                          style={{ maxWidth: "400px" }}
                        >
                          <span className="fw-bold"> Desc:</span>
                          <span className="text-secondary ms-2">
                            {a?.Description}
                          </span>
                        </div>
                      </div>
                    );
                  }}
                  inputType="select"
                />
                <button
                  className="btn btn-colored text-nowrap"
                  disabled={newChildAssets.length === 0}
                  onClick={async () => {
                    const res = await setAsParentAsset({
                      assetUuid: selectedAsset?.Uuid || "",
                      children: newChildAssets,
                    });
                    if ("data" in res) {
                      setNewChildAssets([]);
                    }
                  }}
                >
                  Add dependant assets
                </button>
              </div>
            </div>
            {childrenAssets?.length > 0 ? (
              <AssetList
                showTable
                assets={childrenAssets}
                actions={(a: AssetType) => (
                  <button
                    className="btn text-navy"
                    onClick={() => {
                      removeChildrenFromParentAsset({
                        assetUuid: selectedAsset?.Uuid || "",
                        children: [a.Uuid].filter(
                          (uuid): uuid is string => !!uuid
                        ),
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="lg" />
                  </button>
                )}
              />
            ) : (
              <p className="text-muted">No dependant assets found</p>
            )}
          </div>
        </div>
      )}
      <hr className="border-secondary-subtle" />
      {relationType !== "dependant" && (
        <div className="bg-purple-gradient-darker-reverse p-3 rounded-4 my-3">
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
              <div>
                <div className="fw-bold fs-5"> Connected Assets</div>
                <div className="text-secondary">
                  Select one or more assets that you want to group together
                </div>
              </div>
              <div className="d-flex gap-1">
                <Input
                  // label="Parent assets"
                  key={childrenAssets?.length + parentAssets?.length}
                  placeholder="Select connected assets"
                  value={newParentAssets}
                  onChange={setNewParentAssets}
                  options={(assets.data?.assets || [])
                    .filter((a) => a.Uuid !== selectedAsset?.Uuid)
                    .filter(
                      (a) =>
                        !childrenAssets?.some((child) => child.Uuid === a.Uuid)
                    )
                    .filter(
                      (a) =>
                        !parentAssets?.some((parent) => parent.Uuid === a.Uuid)
                    )
                    .map((a: any) => ({
                      label: a.Label,
                      value: a.Uuid,
                    }))}
                  formatOptionLabel={({ label, value }: any) => {
                    const a = assets.data?.assets.find(
                      (asset) => asset.Uuid === value
                    );
                    return (
                      <div>
                        <div>
                          <span className="fw-bold"> Name:</span>
                          <span className="text-secondary ms-2">
                            {a?.Label}
                          </span>
                        </div>
                        <div>
                          <span className="fw-bold"> Ref:</span>
                          <span className="text-secondary ms-2">{a?.Uuid}</span>
                        </div>
                        <span className="fw-bold"> Cust Ref:</span>
                        <span className="text-secondary ms-2">
                          {a?.Reference}
                        </span>

                        <div
                          className="text-truncate"
                          style={{ maxWidth: "400px" }}
                        >
                          <span className="fw-bold"> Desc:</span>{" "}
                          <span className="text-secondary ms-2">
                            {a?.Description}
                          </span>
                        </div>
                      </div>
                    );
                  }}
                  inputType="select"
                />
                <button
                  className="btn btn-colored text-nowrap"
                  disabled={newParentAssets.length === 0}
                  onClick={async () => {
                    const res = await setAsChildAsset({
                      assetUuid: selectedAsset?.Uuid || "",
                      parents: newParentAssets,
                    });
                    if ("data" in res) {
                      setNewParentAssets([]);
                    }
                  }}
                >
                  Add connected assets
                </button>
              </div>
            </div>
            {parentAssets?.length > 0 ? (
              <AssetList
                showTable
                assets={parentAssets}
                actions={(a: AssetType) => (
                  <button
                    className="btn text-navy"
                    onClick={() => {
                      removeParentsFromChildAsset({
                        assetUuid: selectedAsset?.Uuid || "",
                        parents: [a.Uuid].filter(
                          (uuid): uuid is string => !!uuid
                        ),
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="lg" />
                  </button>
                )}
              />
            ) : (
              <p className="text-muted">No connected assets found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
